import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAckSeasonRecap, useFlags, useSeasonRecap } from '../../api/hooks';
import { setCelebrationPending, useCelebrationSourceIdle } from '../../celebrationGate';
import { haptics } from '../../telegram';
import { track } from '../../analytics';
import { RARITY_TEXT_DARK, rarityTint } from '../cosmetics/cosmeticsMap';
import { RarityBadge } from './RarityBadge';
import { SlotGlyph } from './RewardPreview';
import { SnowIcon } from '../../screens/PassScreen';

const FREEZE_NAMES: Record<string, string> = {
  game1: 'На 1 игру',
  game3: 'На 3 игры',
  week: 'На неделю',
};

/**
 * 7.11 «Итоги сезона»: первый вход после конца сезона — сколько колод
 * открылось само, сетка выпавшего (новые/повторы), сгоревшие заморозки.
 * Показывается после всех прочих празднований (celebrationGate).
 */
export function SeasonRecap({ enabled }: { enabled: boolean }) {
  const flags = useFlags();
  const flagOn = Boolean(flags.data?.pass);
  const recap = useSeasonRecap(enabled && flagOn);
  const ack = useAckSeasonRecap();
  const othersIdle = useCelebrationSourceIdle('achievements');
  const ratingIdle = useCelebrationSourceIdle('rating');
  const plusIdle = useCelebrationSourceIdle('pass-plus');
  const introIdle = useCelebrationSourceIdle('intro');
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  // Появление ячеек по очереди (шаг ~380 мс, легендарные последними) +
  // золотая вспышка в конце.
  const [shown, setShown] = useState(0);
  const [flash, setFlash] = useState(false);

  const data = recap.data?.recap ?? null;
  const hasData = Boolean(enabled && flagOn && !dismissed && data);
  const visible = hasData && othersIdle && ratingIdle && plusIdle && introIdle;
  useEffect(() => {
    setCelebrationPending('pass-recap', hasData);
    return () => setCelebrationPending('pass-recap', false);
  }, [hasData]);
  const total = data?.drops.length ?? 0;
  useEffect(() => {
    if (!visible || total === 0) return;
    const reduce =
      typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setShown(total);
      setFlash(true); // reduce motion: мягкая засветка 0,5 с (CSS fallback)
      return;
    }
    const t = window.setInterval(() => {
      setShown((n) => {
        if (n + 1 >= total) {
          window.clearInterval(t);
          setFlash(true);
          haptics.success();
        }
        return n + 1;
      });
    }, 380);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, total]);
  if (!visible || !data) return null;

  // Легендарные — последними (7.11).
  const drops = [...data.drops].sort(
    (a, b) => (a.item?.rarity ?? a.deckRarity) - (b.item?.rarity ?? b.deckRarity),
  );

  const close = (toWardrobe: boolean) => {
    haptics.tap();
    track('pass_season_recap_ack', { drops: data.drops.length });
    void ack.mutateAsync().catch(() => undefined);
    setDismissed(true);
    if (toWardrobe) navigate('/pass/wardrobe');
  };

  return (
    <div className="bp-recap" data-theme="dark">
      <div className="bp-recap-inner">
        {/* шапка-карточка с золотым градиентом (макет 8) */}
        <div className="bp-recap-head">
          <div className="bp-hero-label">СЕЗОН ЗАВЕРШЁН · {data.name.toUpperCase()}</div>
          <h2>Итоги сезона</h2>
          <div className="bp-recap-stats">
            {data.autoOpened > 0 && (
              <span>
                <b className="mono">{data.autoOpened}</b>колод открыто
              </span>
            )}
            {data.level != null && (
              <span>
                <b className="mono">{data.level}</b>уровень пасса
              </span>
            )}
            {(data.wardrobeCount ?? 0) > 0 && (
              <span>
                <b className="mono">{data.wardrobeCount}</b>в гардеробе
              </span>
            )}
          </div>
        </div>
        <div className="bp-overline" style={{ margin: '14px 0 8px' }}>ВЫПАЛО ИЗ КОЛОД</div>
        <div className="bp-recap-gridwrap">
        <div className="bp-recap-grid">
          {/* Сгоревшие заморозки — первыми, легендарный остаётся последним */}
          {data.burnedFreezes.map((b, i) => (
            <div key={`b${i}`} className={`bp-recap-cell burned${i < shown ? ' in' : ''}`}>
              <span className="box"><SnowIcon size={18} /></span>
              <span className="nm">
                <s>
                  {FREEZE_NAMES[b.kind] ?? b.kind}
                  {b.count > 1 ? ` ×${b.count}` : ''}
                </s>
              </span>
              <span className="tag">Сгорела</span>
            </div>
          ))}
          {drops.map((d, i) => {
            const r = d.item?.rarity ?? d.deckRarity;
            const isLeg = r === 3 && d.resultKind !== 'dup';
            return (
              <div
                key={i}
                className={`bp-recap-cell${i < shown ? ' in' : ''}${isLeg ? ' leg' : ''}`}
              >
                <span
                  className={`box${d.resultKind === 'dup' ? ' dup' : ''}`}
                  style={{
                    // тёмный оверлей — текстовый цвет редкости тёмной темы
                    color: RARITY_TEXT_DARK[r],
                    borderColor: rarityTint(r, 0.55),
                    background: rarityTint(r, 0.14),
                  }}
                >
                  {d.resultKind === 'freeze' ? <SnowIcon size={18} /> : <SlotGlyph slot={d.item?.slot ?? 'nick'} size={18} />}
                  <RarityBadge rarity={r} compact />
                </span>
                <span className="nm">
                  {d.resultKind === 'freeze'
                    ? (FREEZE_NAMES[d.freezeKind ?? ''] ?? 'Заморозка')
                    : (d.item?.name ?? '—')}
                </span>
                {d.resultKind === 'dup' ? (
                  <span className="tag">Повтор +50 XP</span>
                ) : d.resultKind === 'freeze' ? null : (
                  <span className={`tag new${isLeg ? ' gold' : ''}`}>Новое</span>
                )}
              </div>
            );
          })}
          {data.drops.length === 0 && <p>В этом сезоне колод не было.</p>}
        </div>
        {flash && <div className="bp-recap-flash" aria-hidden />}
        </div>
        <button className="btn bp-claim-all" onClick={() => close(true)}>
          В гардероб
        </button>
        <button className="bp-plusfx-skip" onClick={() => close(false)}>
          Закрыть
        </button>
      </div>
    </div>
  );
}
